import CscaExplanationLanguageSwitch, { useCscaExplanationLanguage } from '../components/CscaExplanationLanguage';
import { isCscaPendingReview } from '../utils/cscaTestPresentation';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../store';
import { api } from '../utils/api';
import { TestSession } from '../types';
import { isAdminRole, isTeacherRole } from '../utils/permissions';
import { buildCorrectSequence, isSequenceReviewQuestion } from '../utils/sequenceQuestionScoring';
import { resolveQuestionImagePath } from '../utils/simulationQuestionPresentation';
import MathText from '../components/MathText';
import { getCscaModeDetail, getCscaTestTitle } from '../utils/cscaTestPresentation';
import { CheckCircle, AlertCircle, HelpCircle, ChevronLeft, RefreshCcw, Save, Pencil, X } from 'lucide-react';

const AdminTestDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { token, user } = useAuthStore();

    const [detailSession, setDetailSession] = useState<TestSession | null>(null);
    const [loading, setLoading] = useState(true);
    const { language: explanationLanguage, changeLanguage: changeExplanationLanguage } = useCscaExplanationLanguage();
    const [error, setError] = useState('');
    const [reviewIndex, setReviewIndex] = useState(0);
    const [selectedCorrectAnswers, setSelectedCorrectAnswers] = useState<Record<string, number>>({});
    const [questionDrafts, setQuestionDrafts] = useState<Record<string, { question: string; options: string[]; correctAnswer: number }>>({});
    const [editingQuestionIds, setEditingQuestionIds] = useState<Record<string, boolean>>({});
    const [savingQuestionId, setSavingQuestionId] = useState<string | null>(null);
    const [savingQuestionContentId, setSavingQuestionContentId] = useState<string | null>(null);
    const [recalcLoading, setRecalcLoading] = useState(false);
    const [markingReviewed, setMarkingReviewed] = useState(false);
    const [actionMessage, setActionMessage] = useState('');
    const [actionError, setActionError] = useState('');
    const isAdmin = isAdminRole(user?.role);
    const isTeacher = isTeacherRole(user?.role);
    const locationState = (location.state || {}) as {
        returnTo?: string;
        user?: { id?: string; username?: string };
        testSummary?: {
            id: string;
            userId?: string;
            level?: number;
            part?: string;
            weightedScore?: number;
            maxScore?: number;
            completedAt?: string;
            reviewStatus?: 'pending' | 'reviewed';
            reviewedBy?: string | null;
            reviewedAt?: string | null;
        };
    };
    const initialTestSummary = locationState.testSummary;

    // BACKEND_URL is defined in vite.config.ts globally
    const API_BASE_URL = BACKEND_URL;
    const isAiScoredWritingPrompt = (q: any) => Number(q?.level ?? detailSession?.level ?? 0) === 4 && String(q?.part || '').toUpperCase() === 'W2';
    const getStoredWritingAnswer = (item: any) => {
        const content = item?.answer_content;
        if (Array.isArray(content)) return { sequence: content, isCorrect: !!item?.correct };
        if (content && typeof content === 'object') {
            return {
                sequence: Array.isArray(content.sequence) ? content.sequence : undefined,
                text: typeof content.text === 'string' ? content.text : undefined,
                isCorrect: !!item?.correct,
                aiScore: typeof content.ai_score === 'number' ? content.ai_score : null,
                awardedScore: typeof item?.awarded_score === 'number'
                    ? item.awarded_score
                    : (typeof content.awarded_score === 'number' ? content.awarded_score : null),
                maxScore: typeof content.max_score === 'number' ? content.max_score : null,
                grading: content.ai_grading || null,
            };
        }
        if (typeof content === 'string' && content.trim()) return { text: content, isCorrect: !!item?.correct };
        return null;
    };
    const getQuestionSnapshotPayload = (
        question: TestSession['questions'][number],
        overrides?: Partial<{
            question: string;
            options: string[];
            correctAnswer: number;
            bankVersion: string;
        }>
    ) => ({
        question_id: question.id,
        level: Number(question.level || detailSession?.level || 0),
        part: String(question.part || ''),
        question_text: String(overrides?.question ?? question.question ?? '').trim(),
        media: question.media ?? null,
        options: Array.isArray(overrides?.options) ? overrides.options : [...(question.options || [])],
        correct_index: Number(overrides?.correctAnswer ?? question.correctAnswer ?? 0),
        vocab_ids: Array.isArray(question.vocabIds) ? question.vocabIds : [],
        explanation: question.explanation ?? null,
        question_group: question.questionGroup ?? null,
        group_seq: question.groupSeq ?? null,
        bank_version: String(overrides?.bankVersion ?? question.bankVersion ?? detailSession?.bankVersion ?? '2.0')
    });

    const loadDetail = React.useCallback(async () => {
        if (!id || !token) return;

        setLoading(true);
        setError('');
        setDetailSession(null);
        setActionMessage('');
        setActionError('');
        try {
            const data = await api.getAdminTestDetail(id, token);
            
            if (!data || !data.id) {
                throw new Error('Invalid test data received');
            }

            // Map backend response to TestSession
            const session: TestSession = {
                id: data.id,
                userId: data.user_id,
                level: data.level,
                part: data.part,
                bankVersion: String(data.bank_version || '2.0'),
                systemType: String(data.system_type || 'simulation'),
                workbookPart: data.workbook_part ?? null,
                paperId: data.paper_id ?? null,
                paperYear: data.paper_year == null ? null : Number(data.paper_year),
                paperMonth: data.paper_month == null ? null : Number(data.paper_month),
                topicCoarse: data.topic_coarse ?? null,
                topicFine: data.topic_fine ?? null,
                reviewStatus: data.review_status === 'reviewed' ? 'reviewed' : 'pending',
                reviewedBy: data.reviewed_by ?? null,
                reviewedAt: data.reviewed_at ?? null,
                questions: (data.items || []).map((item: any) => ({
                    id: item.question_id || item.id,
                    level: Number(item.level || data.level || 0),
                    part: item.part || data.part,
                    question: item.question_text || item.question || 'Question text unavailable',
                    options: item.options ? (typeof item.options === 'string' ? JSON.parse(item.options) : item.options) : [],
                    gradingPending: item.grading_pending,
                    correctAnswer: item.correct_option_index ?? item.correctAnswer ?? -1,
                correctAnswers: item.correct_option_indices,
                    historicalVersion: item.historical_version,
                    explanation: item.explanation,
                    explanationId: item.explanation_id || undefined,
                    media: item.media,
                    bankVersion: String(item.bank_version || data.bank_version || '2.0'),
                    vocabIds: Array.isArray(item.vocab_ids)
                        ? item.vocab_ids
                        : (typeof item.vocab_ids === 'string' ? JSON.parse(item.vocab_ids) : []),
                    questionGroup: item.question_group ?? null,
					groupSeq: item.group_seq ?? null,
					reviewCorrect: typeof item.correct === 'boolean' ? item.correct : undefined
                })),
                answers: (data.items || []).map((item: any) => {
                        const storedWritingAnswer = getStoredWritingAnswer(item);
                        if (storedWritingAnswer) return storedWritingAnswer;
                        
                        return item.chosen_option_index ?? (item.chose_buzhidao ? -1 : null);
                }),
                score: data.score,
                completedAt: data.completed_at || data.date_taken,
                createdAt: data.date_taken,
                weightedScore: Number(data.weighted_score || 0),
                maxScore: Number(data.max_score || 0),
                sectionScores: data.section_scores,
            };

            setDetailSession(session);
            setSelectedCorrectAnswers(
                Object.fromEntries(session.questions.map((q) => [q.id, q.correctAnswer]))
            );
            setQuestionDrafts(
                Object.fromEntries(
                    session.questions.map((q) => [q.id, { question: q.question, options: [...q.options], correctAnswer: q.correctAnswer }])
                )
            );
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Failed to load test details');
        } finally {
            setLoading(false);
        }
    }, [id, token]);

    useEffect(() => {
        if (!id || !token) return;
        loadDetail();
    }, [id, token, loadDetail]);

    const scrollToCard = (i: number) => {
        const el = document.getElementById(`q-${i}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setReviewIndex(i);
    };

    const handleCorrectAnswerSelection = (questionId: string, value: number) => {
        setSelectedCorrectAnswers((prev) => ({ ...prev, [questionId]: value }));
    };

    const handleQuestionTextChange = (questionId: string, value: string) => {
        setQuestionDrafts((prev) => ({
            ...prev,
            [questionId]: {
                question: value,
                options: prev[questionId]?.options || [],
                correctAnswer: prev[questionId]?.correctAnswer ?? 0
            }
        }));
    };

    const handleQuestionOptionChange = (questionId: string, optionIndex: number, value: string) => {
        setQuestionDrafts((prev) => {
            const currentOptions = [...(prev[questionId]?.options || [])];
            currentOptions[optionIndex] = value;
            return {
                ...prev,
                [questionId]: {
                    question: prev[questionId]?.question || '',
                    options: currentOptions,
                    correctAnswer: prev[questionId]?.correctAnswer ?? 0
                }
            };
        });
    };

    const handleDraftCorrectAnswerChange = (questionId: string, value: number) => {
        setQuestionDrafts((prev) => ({
            ...prev,
            [questionId]: {
                question: prev[questionId]?.question || '',
                options: prev[questionId]?.options || [],
                correctAnswer: value
            }
        }));
    };

    const handleAddDraftOption = (question: TestSession['questions'][number]) => {
        setQuestionDrafts((prev) => {
            const current = prev[question.id] || {
                question: question.question,
                options: [...question.options],
                correctAnswer: question.correctAnswer
            };
            return {
                ...prev,
                [question.id]: {
                    ...current,
                    options: [...current.options, '']
                }
            };
        });
    };

    const handleRemoveDraftOption = (question: TestSession['questions'][number], optionIndex: number) => {
        setQuestionDrafts((prev) => {
            const current = prev[question.id] || {
                question: question.question,
                options: [...question.options],
                correctAnswer: question.correctAnswer
            };
            if (current.options.length <= 1) {
                return prev;
            }

            const nextOptions = current.options.filter((_, index) => index !== optionIndex);
            const nextCorrectAnswer = current.correctAnswer > optionIndex
                ? current.correctAnswer - 1
                : Math.min(current.correctAnswer, nextOptions.length - 1);

            return {
                ...prev,
                [question.id]: {
                    question: current.question,
                    options: nextOptions,
                    correctAnswer: nextCorrectAnswer
                }
            };
        });
    };

    const handleToggleQuestionEditor = (question: TestSession['questions'][number]) => {
        setEditingQuestionIds((prev) => {
            const nextOpen = !prev[question.id];
            const nextState = { ...prev, [question.id]: nextOpen };
            if (nextOpen) {
                setQuestionDrafts((drafts) => ({
                    ...drafts,
                    [question.id]: {
                        question: question.question,
                        options: [...question.options],
                        correctAnswer: question.correctAnswer
                    }
                }));
            }
            return nextState;
        });
    };

    const handleUpdateCorrectAnswer = async (question: TestSession['questions'][number]) => {
        if (!token) return;

        const nextCorrectAnswer = selectedCorrectAnswers[question.id];
        if (!Number.isInteger(nextCorrectAnswer) || nextCorrectAnswer < 0) {
            setActionError('Please select a valid answer first.');
            return;
        }

        if (nextCorrectAnswer === question.correctAnswer) {
            setActionMessage(`Question ${question.id} is already set to ${String.fromCharCode(65 + nextCorrectAnswer)}.`);
            setActionError('');
            return;
        }

        const fromLabel = question.correctAnswer >= 0 ? String.fromCharCode(65 + question.correctAnswer) : 'Unknown';
        const toLabel = String.fromCharCode(65 + nextCorrectAnswer);
        const confirmed = window.confirm(
            isAdmin
                ? `Confirm updating the correct answer in D1 for question ${question.id} from ${fromLabel} to ${toLabel}?`
                : `Submit a change request for question ${question.id} to update the correct answer from ${fromLabel} to ${toLabel}?`
        );
        if (!confirmed) return;

        try {
            setSavingQuestionId(question.id);
            setActionError('');
            setActionMessage('');
            if (isAdmin) {
                await api.updateAdminQuestionCorrectAnswer(question.id, nextCorrectAnswer, token);
                setDetailSession((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        questions: prev.questions.map((q) =>
                            q.id === question.id ? { ...q, correctAnswer: nextCorrectAnswer } : q
                        )
                    };
                });
                setActionMessage(`Question ${question.id} correct answer updated. Click "Update Score" to sync the stored score and composite stats.`);
            } else {
                await api.createQuestionChangeRequest({
                    question_id: question.id,
                    bankVersion: String(question.bankVersion || detailSession?.bankVersion || '2.0'),
                    change_type: 'update',
                    proposed_snapshot: getQuestionSnapshotPayload(question, { correctAnswer: nextCorrectAnswer })
                }, token);
                setActionMessage(`Question ${question.id} change request submitted for admin approval. The live question stays unchanged until it is approved.`);
            }
        } catch (err: any) {
            console.error(err);
            setActionError(err?.message || (isAdmin ? 'Failed to update correct answer.' : 'Failed to submit change request.'));
        } finally {
            setSavingQuestionId(null);
        }
    };

    const handleUpdateQuestionContent = async (question: TestSession['questions'][number]) => {
        if (!token) return;

        const draft = questionDrafts[question.id];
        const nextQuestionText = String(draft?.question ?? '').trim();
        const nextOptions = (draft?.options || []).map((option) => String(option ?? '').trim());
        const nextCorrectAnswer = Number(draft?.correctAnswer ?? question.correctAnswer ?? 0);

        if (!nextQuestionText) {
            setActionError('Question text cannot be empty.');
            setActionMessage('');
            return;
        }
        if (nextOptions.length === 0 || nextOptions.some((option) => !option)) {
            setActionError('All options must have content.');
            setActionMessage('');
            return;
        }
        if (!Number.isInteger(nextCorrectAnswer) || nextCorrectAnswer < 0 || nextCorrectAnswer >= nextOptions.length) {
            setActionError('Please choose a valid correct answer.');
            setActionMessage('');
            return;
        }

        const contentChanged = nextQuestionText !== question.question
            || nextOptions.length !== question.options.length
            || nextOptions.some((option, index) => option !== question.options[index])
            || nextCorrectAnswer !== question.correctAnswer;

        if (!contentChanged) {
            setActionMessage(`Question ${question.id} content is already up to date.`);
            setActionError('');
            return;
        }

        const confirmed = window.confirm(
            isAdmin
                ? `Save the updated question text and options for question ${question.id} to D1?`
                : `Submit these question changes for admin approval for question ${question.id}?`
        );
        if (!confirmed) return;

        try {
            setSavingQuestionContentId(question.id);
            setActionError('');
            setActionMessage('');
            if (isAdmin) {
                await api.updateAdminQuestionContent(question.id, nextQuestionText, nextOptions, nextCorrectAnswer, token);
                setDetailSession((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        questions: prev.questions.map((q) =>
                            q.id === question.id ? { ...q, question: nextQuestionText, options: nextOptions, correctAnswer: nextCorrectAnswer } : q
                        )
                    };
                });
                setQuestionDrafts((prev) => ({
                    ...prev,
                    [question.id]: {
                        question: nextQuestionText,
                        options: nextOptions,
                        correctAnswer: nextCorrectAnswer
                    }
                }));
                setSelectedCorrectAnswers((prev) => ({
                    ...prev,
                    [question.id]: nextCorrectAnswer
                }));
                setActionMessage(`Question ${question.id} content updated in D1. If this change affects scoring, click "Update Score".`);
            } else {
                await api.createQuestionChangeRequest({
                    question_id: question.id,
                    bankVersion: String(question.bankVersion || detailSession?.bankVersion || '2.0'),
                    change_type: 'update',
                    proposed_snapshot: getQuestionSnapshotPayload(question, {
                        question: nextQuestionText,
                        options: nextOptions,
                        correctAnswer: nextCorrectAnswer
                    })
                }, token);
                setSelectedCorrectAnswers((prev) => ({
                    ...prev,
                    [question.id]: nextCorrectAnswer
                }));
                setActionMessage(`Question ${question.id} change request submitted for admin approval. The live question stays unchanged until it is approved.`);
            }
        } catch (err: any) {
            console.error(err);
            setActionError(err?.message || (isAdmin ? 'Failed to update question content.' : 'Failed to submit change request.'));
        } finally {
            setSavingQuestionContentId(null);
        }
    };

    const handleRecalculateScore = async () => {
        if (!id || !token) return;
        const confirmed = window.confirm('Recalculate this test score using the latest D1 correct answers and refresh the student composite stats?');
        if (!confirmed) return;

        try {
            setRecalcLoading(true);
            setActionError('');
            setActionMessage('');
            await api.recalculateAdminTestScore(id, token);
            await loadDetail();
            setActionMessage('This test score and related composite stats have been updated.');
        } catch (err: any) {
            console.error(err);
            setActionError(err?.message || 'Failed to update score.');
        } finally {
            setRecalcLoading(false);
        }
    };

    const handleMarkReviewed = async () => {
        if (!id || !token || (!isTeacher && !isAdmin)) return;
        const confirmed = window.confirm('Mark this test as reviewed? Teachers and admins will be able to see the reviewed status.');
        if (!confirmed) return;

        try {
            setMarkingReviewed(true);
            setActionError('');
            setActionMessage('');
            const response = await api.markAdminTestReviewed(id, token);
            setDetailSession((prev) => prev ? {
                ...prev,
                reviewStatus: response.test.review_status === 'reviewed' ? 'reviewed' : 'pending',
                reviewedBy: response.test.reviewed_by ?? null,
                reviewedAt: response.test.reviewed_at ?? null
            } : prev);
            setActionMessage('This test has been marked as reviewed. Teachers and admins can now see the reviewed status.');
        } catch (err: any) {
            console.error(err);
            setActionError(err?.message || 'Failed to mark test reviewed.');
        } finally {
            setMarkingReviewed(false);
        }
    };

    const handleBack = () => {
        const userIdForBack = detailSession?.userId || initialTestSummary?.userId;
        if (userIdForBack) {
            navigate(locationState.returnTo || `/admin/users/${userIdForBack}/history`, {
                state: {
                    user: locationState.user,
                    updatedReview: detailSession
                        ? {
                            id: detailSession.id,
                            review_status: detailSession.reviewStatus === 'reviewed' ? 'reviewed' : 'pending',
                            reviewed_by: detailSession.reviewedBy ?? null,
                            reviewed_at: detailSession.reviewedAt ?? null,
                        }
                        : initialTestSummary
                            ? {
                                id: initialTestSummary.id,
                                review_status: initialTestSummary.reviewStatus === 'reviewed' ? 'reviewed' : 'pending',
                                reviewed_by: initialTestSummary.reviewedBy ?? null,
                                reviewed_at: initialTestSummary.reviewedAt ?? null,
                            }
                            : undefined,
                }
            });
            return;
        }
        navigate(-1);
    };

    if (loading) {
        const initialReviewStatus = initialTestSummary?.reviewStatus === 'reviewed' ? 'reviewed' : 'pending';
        return (
          <div className="max-w-6xl mx-auto pb-12 px-4">
            <div className="bg-white shadow-xl shadow-indigo-500/5 rounded-3xl p-8 border border-indigo-50">
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
                    <div>
                         <button
                            onClick={handleBack}
                            className="flex items-center text-gray-500 hover:text-[#4F46E5] mb-2 transition-colors font-bold"
                         >
                             <ChevronLeft className="w-5 h-5 mr-1" />
                             Back
                         </button>
                        <h1 className="text-3xl font-bold text-[#312E81]">
                            Test Review <span className="text-lg font-normal text-gray-500">(User: {initialTestSummary?.userId || '-'})</span>
                        </h1>
                        {typeof initialTestSummary?.weightedScore === 'number' && typeof initialTestSummary?.maxScore === 'number' && (
                            <div className="flex gap-4 mt-1">
                                <p className="text-[#4F46E5] font-semibold">
                                    Score: {initialTestSummary.weightedScore}/{initialTestSummary.maxScore}
                                </p>
                            </div>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                            <span className={`inline-flex items-center rounded-full px-3 py-1 font-semibold ${
                                initialReviewStatus === 'reviewed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-amber-100 text-amber-800'
                            }`}>
                                {initialReviewStatus === 'reviewed' ? 'Reviewed' : 'Pending Review'}
                            </span>
                        </div>
                    </div>
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#4F46E5] mx-auto mb-4"></div>
                      <p className="text-[#312E81] font-bold">Loading test details...</p>
                    </div>
                </div>
            </div>
          </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-4xl mx-auto mt-8 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg">
                Error: {error}
            </div>
        );
    }

    if (!detailSession) return null;

    const total = detailSession.questions.length;
    const sessionPart = String(detailSession.part || '').toLowerCase();
    const UNKNOWN_ANSWER = -1;
    const getQuestionWeight = (q: any, idx: number) => {
        if (detailSession.bankVersion === 'CSCA') { const graded = detailSession.questions.filter(q => !isCscaPendingReview(q)).length; return !isCscaPendingReview(q) && graded > 0 ? 100 / graded : 0; }
        const p = String(q.part || '').toUpperCase();
        if (Number(detailSession.level || 0) === 4) {
            const hsk4Weights: Record<string, number> = {
                L1: 2,
                L2: 2,
                L3: 2,
                L4: 3,
                R1: 2,
                R2: 2,
                R3: 2,
                R4: 3,
                R5: 3,
                W1: 6,
                W2: 8,
            };
            if (hsk4Weights[p] != null) return hsk4Weights[p];
        }
        if (['L1', 'L2', 'R2'].some(x => p.startsWith(x))) return 2;
        if (['L3', 'L4', 'R1'].some(x => p.startsWith(x))) return 3;
        if (['R3', 'R4', 'R5'].some(x => p.startsWith(x))) return 4;
        if (p === 'W1') return 12;
        if (p === 'W2') return 8;
        const oneBased = idx + 1;
        if (sessionPart === 'simulation') {
            if (oneBased >= 1 && oneBased <= 20) return 2;
            if (oneBased >= 21 && oneBased <= 50) return 3;
            if (oneBased >= 51 && oneBased <= 55) return 2;
            if (oneBased >= 56 && oneBased <= 70) return 4;
            if (oneBased >= 71 && oneBased <= 75) return 12;
            if (oneBased >= 76 && oneBased <= 80) return 8;
        }
        return 1;
    };
    
    const isCorrect = (i: number) => {
        const q = detailSession.questions[i];
		if (typeof q?.reviewCorrect === 'boolean') return q.reviewCorrect;
        const ans = detailSession.answers[i];
        if (ans && typeof ans === 'object' && 'isCorrect' in ans) {
            return (ans as any).isCorrect === true;
        }
        const isWriting = String(q.part || '').toLowerCase().startsWith('w') || sessionPart === 'writing';
        if (isWriting && !isAiScoredWritingPrompt(q)) {
            if (typeof ans === 'object' && ans !== null) {
                const a = ans as any;
                if (a.sequence) {
                     const userSeq = a.sequence || [];
                     const correctSeq = q.options || [];
                     return userSeq.length === correctSeq.length && correctSeq.every((t: string, k: number) => t === userSeq[k]);
                } else if (a.text) {
                     const userText = String(a.text || '').trim();
                     const correctText = String(q.options?.[0] || '').trim();
                     return userText === correctText;
                }
            }
        }
        
        return ans !== null && ans !== UNKNOWN_ANSWER && (detailSession.questions[i].correctAnswers || [detailSession.questions[i].correctAnswer]).includes(ans as number);
    };
	const isUnanswered = (i: number) => {
        if (isCscaPendingReview(detailSession.questions[i])) return false;
		const answer = detailSession.answers[i] as any;
		if (answer == null) return true;
		if (typeof answer !== 'object') return false;
		const hasSequence = Array.isArray(answer.sequence) && answer.sequence.length > 0;
		const hasText = typeof answer.text === 'string' && answer.text.trim().length > 0;
		return !hasSequence && !hasText;
	};
	const isUnknown = (i: number) => detailSession.answers[i] === UNKNOWN_ANSWER || isUnanswered(i);
    const isAnsweredWrong = (i: number) => !isCscaPendingReview(detailSession.questions[i]) && !isCorrect(i) && !isUnknown(i);
    const getAwardedScore = (i: number) => {
        const ans = detailSession.answers[i];
        const weight = getQuestionWeight(detailSession.questions[i], i);
        if (ans && typeof ans === 'object' && typeof (ans as any).awardedScore === 'number') {
            return Number((ans as any).awardedScore || 0);
        }
        return isCorrect(i) ? weight : 0;
    };
    const weightedScore = typeof detailSession.weightedScore === 'number'
        ? Number(detailSession.weightedScore || 0)
        : detailSession.questions.reduce((sum, _q, i) => sum + getAwardedScore(i), 0);
    const maxWeightedScore = typeof detailSession.maxScore === 'number' && detailSession.maxScore > 0
        ? Number(detailSession.maxScore || 0)
        : detailSession.questions.reduce((sum, q, i) => sum + getQuestionWeight(q, i), 0);
    const score = detailSession.questions.reduce((sum, _q, i) => sum + (isCorrect(i) ? 1 : 0), 0);
    const reviewStatus = detailSession.reviewStatus === 'reviewed' ? 'reviewed' : 'pending';
    const isCsca = detailSession.bankVersion === 'CSCA';
    const cscaModeDetail = isCsca ? getCscaModeDetail(detailSession) : '';

	const isOrderQuestion = (q: any) => isSequenceReviewQuestion(q, detailSession.level);
	const isWritingPart = (q: any) => isOrderQuestion(q) || String(q.part || '').toLowerCase().startsWith('w') || sessionPart === 'writing';

    return (
        <div className="max-w-6xl mx-auto pb-12 px-4">
            <div className="bg-white shadow-xl shadow-indigo-500/5 rounded-3xl p-8 border border-indigo-50">
                {actionMessage && (
                    <div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                        {actionMessage}
                    </div>
                )}
                {actionError && (
                    <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {actionError}
                    </div>
                )}
                <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
                    <div>
                         <button 
                            onClick={handleBack}
                            className="flex items-center text-gray-500 hover:text-[#4F46E5] mb-2 transition-colors font-bold"
                         >
                             <ChevronLeft className="w-5 h-5 mr-1" />
                             Back
                         </button>
                        <h1 className="text-3xl font-bold text-[#312E81]">
                            {isCsca ? getCscaTestTitle(detailSession) : 'Test Review'} <span className="text-lg font-normal text-gray-500">(User: {detailSession.userId})</span>
                        </h1>
                        {isCsca && <CscaExplanationLanguageSwitch language={explanationLanguage} onChange={changeExplanationLanguage} />}
                        {isCsca && cscaModeDetail && (
                            <p className="mt-1 text-sm font-medium text-gray-500">{cscaModeDetail}</p>
                        )}
                        <div className="flex gap-4 mt-1">
                            <p className="text-[#4F46E5] font-semibold">
                                Correct: {score}/{total}
                            </p>
                            <p className="text-[#4F46E5] font-semibold border-l pl-4 border-indigo-100">
                                Score: {weightedScore}/{maxWeightedScore}
                            </p>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                            <span className={`inline-flex items-center rounded-full px-3 py-1 font-semibold ${
                                reviewStatus === 'reviewed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-amber-100 text-amber-800'
                            }`}>
                                {reviewStatus === 'reviewed' ? 'Reviewed' : 'Pending Review'}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-3">
                        {(isTeacher || isAdmin) && reviewStatus !== 'reviewed' && (
                            <button
                                onClick={handleMarkReviewed}
                                disabled={markingReviewed}
                                className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                {markingReviewed ? 'Saving Review...' : 'Mark Reviewed'}
                            </button>
                        )}
                        {isAdmin && (
                            <button
                                onClick={handleRecalculateScore}
                                disabled={recalcLoading}
                                className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <RefreshCcw className={`mr-2 h-4 w-4 ${recalcLoading ? 'animate-spin' : ''}`} />
                                {recalcLoading ? 'Updating Score...' : 'Update Score'}
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                    {/* Sidebar Question Map */}
                    <div className="md:col-span-3">
                        <div className="sticky top-24 bg-gray-50 rounded-2xl p-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
                            <h3 className="font-bold text-gray-700 mb-4">Question Map</h3>
                            <div className="grid grid-cols-5 gap-2">
                                {detailSession.questions.map((_, i) => {
                                    const btnCorrect = isCorrect(i);
                                    const btnUnknown = isUnknown(i);
                                    const btnWrong = isAnsweredWrong(i);
                                    const base = 'flex items-center justify-center h-10 rounded-lg text-sm font-bold transition-all';
                                    const color = isCscaPendingReview(detailSession.questions[i])
                                        ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        : btnCorrect
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : (btnUnknown)
                                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                            : 'bg-red-100 text-red-700 hover:bg-red-200';
                                    const active = i === reviewIndex ? 'ring-2 ring-offset-2 ring-[#4F46E5]' : '';
                                    return (
                                        <button key={i} onClick={() => scrollToCard(i)} className={`${base} ${color} ${active}`}>{i + 1}</button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Questions List */}
                    <div className="md:col-span-9 space-y-8">
                        {detailSession.questions.map((qItem, idx) => {
                            const ans = detailSession.answers[idx];
                            const writingAnswer = (typeof ans === 'object') ? ans : null;
                            const cardCorrect = isCorrect(idx);
                            const cardUnknown = isUnknown(idx);
                            const aiScoredPrompt = isAiScoredWritingPrompt(qItem);
                            const awardedScore = typeof (writingAnswer as any)?.awardedScore === 'number' ? Number((writingAnswer as any).awardedScore || 0) : null;
                            const maxScore = typeof (writingAnswer as any)?.maxScore === 'number'
                                ? Number((writingAnswer as any).maxScore || 0)
                                : getQuestionWeight(qItem, idx);
                            const grading = (writingAnswer as any)?.grading || null;
                            const aiScore = typeof (writingAnswer as any)?.aiScore === 'number' ? Number((writingAnswer as any).aiScore || 0) : null;
                            const cardClass = isCscaPendingReview(qItem) ? 'border-slate-200' : aiScoredPrompt
                                ? 'border-indigo-200 shadow-indigo-500/5'
                                : cardCorrect ? 'border-green-200 shadow-green-500/5' : 'border-red-200 shadow-red-500/5';
                            
                            const isWriting = isWritingPart(qItem);
                            const orderType = isOrderQuestion(qItem);
							const correctSequence = orderType ? buildCorrectSequence(qItem) : [];
							const questionImagePath = resolveQuestionImagePath(qItem, detailSession.level);
							const isListening = String(qItem.part || '').toUpperCase().startsWith('L') || sessionPart === 'listening';
                            const userSeq = (writingAnswer as any)?.sequence || [];
                            const userText = (writingAnswer as any)?.text || '';
                            const selectedCorrectAnswer = selectedCorrectAnswers[qItem.id] ?? qItem.correctAnswer;
                            const isSavingThisQuestion = savingQuestionId === qItem.id;
                            const isSavingThisQuestionContent = savingQuestionContentId === qItem.id;
                            const draft = questionDrafts[qItem.id] || { question: qItem.question, options: qItem.options, correctAnswer: qItem.correctAnswer };
                            const isEditingQuestion = editingQuestionIds[qItem.id] === true;

                            return (
                                <div key={idx} id={`q-${idx}`} className={`p-6 md:p-8 bg-white border-2 rounded-3xl shadow-sm ${cardClass}`}>
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="px-3 py-1 bg-gray-100 rounded-full text-sm font-bold text-gray-600">
                                            Question {idx + 1}
                                            <span className="text-xs text-gray-400 font-normal ml-2">({qItem.id})</span>
                                        </div>
                                        <div className="text-sm font-bold">
                                            {isCscaPendingReview(qItem) ? <span className="text-slate-600">待核对 · 不计分</span> : aiScoredPrompt && !cardUnknown ? (
                                                <span className="text-indigo-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> AI {awardedScore ?? 0}/{maxScore}</span>
                                            ) : cardCorrect ? (
                                                <span className="text-green-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Correct</span>
                                            ) : cardUnknown ? (
												<span className="text-orange-500 flex items-center gap-1"><HelpCircle className="w-4 h-4" /> {isUnanswered(idx) ? 'Unanswered' : "Don't Know"}</span>
                                            ) : (
                                                <span className="text-red-500 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Incorrect</span>
                                            )}
                                        </div>
                                    </div>
                                    {isCsca ? (
                                        <MathText content={qItem.question} className="mb-6 block text-xl font-medium leading-8 text-[#312E81]" />
                                    ) : (
                                        <div className="mb-6 text-xl font-medium text-[#312E81] whitespace-pre-line">{qItem.question}</div>
                                    )}
									{isWriting && questionImagePath && (
										<div className="mb-6">
											<img
												src={questionImagePath.startsWith('http') ? questionImagePath : `${API_BASE_URL}/media/${questionImagePath.replace(/^\/+/, '')}`}
												alt={`Writing prompt ${idx + 1}`}
												className="max-h-96 w-auto rounded-lg border border-gray-200 bg-gray-50 object-contain"
											/>
										</div>
									)}
									{isListening && qItem.media && (
                                        <div className="mb-6">
                                             <audio controls src={qItem.media.startsWith('http') ? qItem.media : `${API_BASE_URL}/media${qItem.media.startsWith('/') ? '' : '/'}${qItem.media}`} className="w-full rounded-lg" />
                                        </div>
                                    )}
                                    
                                    {isWriting ? (
                                         orderType ? (
                                            <div className="space-y-3">
                                              <div className="p-4 border rounded bg-gray-50">
                                                <div className="text-sm text-gray-600 mb-2">User Answer</div>
                                                <div className="flex flex-wrap gap-2">
                                                  {userSeq.map((t: string, i: number) => (
                                                    <span key={i} className="px-2 py-1 border rounded bg-white shadow-sm">{t}</span>
                                                  ))}
                                                  {userSeq.length === 0 && <span className="text-gray-400 italic">No answer</span>}
                                                </div>
                                              </div>
                                              <div className="p-4 border rounded bg-green-50">
                                                <div className="text-sm text-gray-600 mb-2">Options (Correct Order usually implied)</div>
                                                <div className="flex flex-wrap gap-2">
												   {correctSequence.map((t: string, i: number) => (
                                                     <span key={i} className="px-2 py-1 border rounded bg-white shadow-sm">{t}</span>
                                                   ))}
                                                </div>
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="space-y-3">
                                              <div className="p-4 border rounded bg-gray-50">
                                                <div className="text-sm text-gray-600 mb-2">User Answer</div>
                                                <div className="text-gray-800">{userText || 'No answer'}</div>
                                              </div>
                                              {!aiScoredPrompt ? (
                                                <div className="p-4 border rounded bg-green-50">
                                                  <div className="text-sm text-gray-600 mb-2">Reference Answer</div>
                                                  <div className="text-green-700">{(qItem.options[0] || '')}</div>
                                                </div>
                                              ) : (
                                                <div className="space-y-3">
                                                  {awardedScore != null && (
                                                    <div className="p-4 border rounded bg-indigo-50 border-indigo-100">
                                                      <div className="flex items-center justify-between">
                                                        <div>
                                                          <div className="text-sm font-bold text-indigo-700">AI Score</div>
                                                          <div className="text-xs text-gray-500">AI 百分比分数已折算到本题得分</div>
                                                        </div>
                                                        <div className="text-right">
                                                          <div className="text-2xl font-bold text-[#4F46E5]">{awardedScore}<span className="text-sm text-gray-400">/{maxScore}</span></div>
                                                          {aiScore != null && <div className="text-xs text-gray-500">AI: {aiScore}/100</div>}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )}
                                                  {grading && (
                                                    <div className="p-4 border rounded bg-indigo-50 border-indigo-100 space-y-3">
                                                      <div className="flex items-center justify-between">
                                                        <div className="text-sm font-bold text-indigo-700">AI Analysis</div>
                                                        {typeof grading.score === 'number' && (
                                                          <div className="text-2xl font-bold text-[#4F46E5]">{grading.score}<span className="text-sm text-gray-400">/100</span></div>
                                                        )}
                                                      </div>
                                                      {grading.summary && <div className="text-sm text-gray-700">{grading.summary}</div>}
                                                      {grading.sample && (
                                                        <div className="bg-green-50 rounded p-3">
                                                          <div className="text-sm font-semibold text-green-800 mb-1">Sample</div>
                                                          <div className="text-sm text-gray-700 whitespace-pre-line">{grading.sample}</div>
                                                        </div>
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          )
                                    ) : (
                                        <div className="space-y-3">
                                            {qItem.options.map((opt, oidx) => {
                                                const isChosen = ans === oidx && !isCscaPendingReview(qItem);
                                                const isRight = (qItem.correctAnswers || [qItem.correctAnswer]).includes(oidx);
                                                const isImage = opt && /\.(webp|jpg|png|jpeg)$/i.test(opt);
                                                
                                                let optStyle = "border-gray-100 bg-white";
                                                if (isRight) optStyle = "border-green-500 bg-green-50 ring-1 ring-green-500";
                                                else if (isChosen) optStyle = "border-red-500 bg-red-50 ring-1 ring-red-500";
    
                                                return (
                                                    <div key={oidx} className={`p-4 border-2 rounded-xl transition-all ${optStyle}`}>
                                                        <div className="flex items-center gap-3">
                                                            <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isRight ? 'bg-green-200 text-green-800' : isChosen ? 'bg-red-200 text-red-800' : 'bg-gray-100 text-gray-500'}`}>
                                                                {String.fromCharCode(65 + oidx)}
                                                            </span>
                                                            {isImage ? (
                                                                <img 
                                                                    src={`${API_BASE_URL}/media/${opt}`} 
                                                                    alt={`Option`} 
                                                                    className="h-32 w-auto object-contain rounded-lg border border-gray-100 bg-white"
                                                                    onError={(e) => {
                                                                        (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZD0iTTEyIDJDMTYuNDEgMiAyMCA1LjU5IDIwIDEwVjE0QzIwIDE4LjQxIDE2LjQxIDIyIDEyIDIyQzcuNTkgMjIgNCAxOC40MSA0IDE0VjEwQzQgNS41OSA3LjU5IDIgMTIgMloiIGZpbGw9IiNjY2MiLz48L3N2Zz4='; 
                                                                    }}
                                                                />
                                                            ) : isCsca ? (
                                                                    <MathText content={opt} className="min-w-0 flex-1 text-gray-800 font-medium" />
                                                                ) : (
                                                                    <span className="text-gray-800 font-medium">{opt}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    
                                    {qItem.explanation && (
                                        <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl text-blue-800 text-sm">
                                            <span className="font-bold block mb-1">Explanation:</span>
                                            {isCsca ? <><MathText content={explanationLanguage === 'ID' && qItem.explanationId ? qItem.explanationId : qItem.explanation} />{explanationLanguage === 'ID' && !qItem.explanationId && <p className="mt-2 text-xs">Terjemahan ID belum tersedia; pembahasan CN ditampilkan.</p>}</> : qItem.explanation}
                                        </div>
                                    )}

                                    {qItem.historicalVersion ? <p className="mt-6 text-sm text-slate-500">历史题目版本。当前题库的题目请在题目管理页修改。</p> : <div className="mt-6">
                                        <button
                                            onClick={() => handleToggleQuestionEditor(qItem)}
                                            className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                                        >
                                            {isEditingQuestion ? (
                                                <>
                                                    <X className="mr-2 h-4 w-4" />
                                                    Close Editor
                                                </>
                                            ) : (
                                                <>
                                                    <Pencil className="mr-2 h-4 w-4" />
                                                    Edit Question
                                                </>
                                            )}
                                        </button>
                                    </div>}

                                    {isEditingQuestion && !qItem.historicalVersion && (
                                        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                            <div className="flex flex-col gap-3">
                                                <div>
                                                    <div className="text-sm font-semibold text-slate-900">Edit Question Content</div>
                                                    <p className="mt-1 text-xs text-slate-600">
                                                        {isAdmin
                                                            ? 'You can edit the question text and each option here, then save directly to D1.'
                                                            : 'You can edit the question text and options here, then submit the change for admin approval.'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                        Question Text
                                                    </label>
                                                    <textarea
                                                        value={draft.question}
                                                        onChange={(e) => handleQuestionTextChange(qItem.id, e.target.value)}
                                                        rows={3}
                                                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                                    />
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                        Options
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleAddDraftOption(qItem)}
                                                        className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                                    >
                                                        Add Option
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-1 gap-3">
                                                    {draft.options.map((opt, optionIndex) => (
                                                        <div key={optionIndex} className="rounded-xl border border-slate-200 bg-white p-3">
                                                            <div className="mb-2 flex items-center justify-between">
                                                                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                                                    <input
                                                                        type="radio"
                                                                        name={`draft-correct-${qItem.id}`}
                                                                        checked={draft.correctAnswer === optionIndex}
                                                                        onChange={() => handleDraftCorrectAnswerChange(qItem.id, optionIndex)}
                                                                    />
                                                                    Option {String.fromCharCode(65 + optionIndex)}
                                                                </label>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveDraftOption(qItem, optionIndex)}
                                                                    disabled={draft.options.length <= 1}
                                                                    className="rounded border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                            <input
                                                                type="text"
                                                                value={opt}
                                                                onChange={(e) => handleQuestionOptionChange(qItem.id, optionIndex, e.target.value)}
                                                                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="flex justify-end">
                                                    <button
                                                        onClick={() => handleUpdateQuestionContent(qItem)}
                                                        disabled={isSavingThisQuestionContent}
                                                        className="inline-flex items-center justify-center rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        <Save className="mr-2 h-4 w-4" />
                                                        {isSavingThisQuestionContent
                                                            ? (isAdmin ? 'Saving Content...' : 'Submitting...')
                                                            : (isAdmin ? 'Save Question Content' : 'Submit Change for Approval')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {!qItem.historicalVersion && !isWriting && qItem.options.length > 0 && (
                                        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                                            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                                                <div className="flex-1">
                                                    <div className="text-sm font-semibold text-amber-900">{isAdmin ? 'Modify Correct Answer' : 'Submit Correct Answer Change'}</div>
                                                    <p className="mt-1 text-xs text-amber-700">
                                                        {isAdmin
                                                            ? 'This updates the D1 question bank directly. Then click "Update Score" above to sync this test\'s stored result.'
                                                            : 'This creates an approval request for admin review. The live question bank and stored score do not change until approval.'}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                                    <select
                                                        value={selectedCorrectAnswer}
                                                        onChange={(e) => handleCorrectAnswerSelection(qItem.id, Number(e.target.value))}
                                                        className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                                    >
                                                        {qItem.options.map((_, optionIndex) => (
                                                            <option key={optionIndex} value={optionIndex}>
                                                                {String.fromCharCode(65 + optionIndex)}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        onClick={() => handleUpdateCorrectAnswer(qItem)}
                                                        disabled={isSavingThisQuestion}
                                                        className="inline-flex items-center justify-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        <Save className="mr-2 h-4 w-4" />
                                                        {isSavingThisQuestion
                                                            ? (isAdmin ? 'Saving...' : 'Submitting...')
                                                            : (isAdmin ? 'Modify Correct Answer' : 'Submit Answer Change')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminTestDetail;
